/**
 * Converts #RGB or #RRGGBB to HSL channels for CSS variables: "H S% L%".
 */
export function hexToHslChannels(hex: string): string | null {
  const raw = hex.trim().replace(/^#/, '')
  if (!/^([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(raw)) {
    return null
  }

  const expanded =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw

  const r = parseInt(expanded.slice(0, 2), 16) / 255
  const g = parseInt(expanded.slice(2, 4), 16) / 255
  const b = parseInt(expanded.slice(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min

  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (delta !== 0) {
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min)
    switch (max) {
      case r:
        h = ((g - b) / delta + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / delta + 2) / 6
        break
      default:
        h = ((r - g) / delta + 4) / 6
    }
  }

  const hue = Math.round(h * 360)
  const sat = Math.round(s * 100)
  const light = Math.round(l * 100)

  return `${hue} ${sat}% ${light}%`
}

/** Picks light or dark foreground channels for text on a brand-colored surface using WCAG relative luminance. */
export function pickForegroundChannels(color: string): string {
  const hex = color.trim()
  if (!hex.startsWith('#') && !/^[0-9A-Fa-f]{3,6}$/.test(hex)) {
    // Fallback if HSL channels string was passed directly
    const match = color.match(/(\d+(?:\.\d+)?)%\s*$/)
    const lightness = match ? parseFloat(match[1]) : 50
    return lightness > 55 ? '0 0% 9%' : '0 0% 100%'
  }

  const raw = hex.replace(/^#/, '')
  const expanded =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw

  if (expanded.length !== 6) {
    return '0 0% 100%'
  }

  const r = parseInt(expanded.slice(0, 2), 16) / 255
  const g = parseInt(expanded.slice(2, 4), 16) / 255
  const b = parseInt(expanded.slice(4, 6), 16) / 255

  // Linearize sRGB channels
  const a = [r, g, b].map((v) => {
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  })

  // WCAG relative luminance formula
  const luminance = 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2]
  return luminance > 0.43 ? '0 0% 9%' : '0 0% 100%'
}
